// src/services/preferenceAnalyticsService.ts
//
// Phase 9 (v1.5.0): preference-vs-allocation analytics.
//
// Pure computation from intentions + grid + staff. No data loading here — the page
// passes in the data it loaded via existing services. Keeps this service testable
// and decouples it from context specifics.

import { TeachingIntention } from '../types/intentions';
import { StaffAllocationGrid } from '../types/grid';
import { DayOffPreference } from '../types/intentionPeriods';

export interface TeacherMatchRow {
  staffId: string;
  staffName: string;
  department: string;

  // FTE match
  preferredFte: number | null;
  preferredFteLabel: string;           // '1.0' | '0.75' | '0.5' | 'discuss' | '—'
  allocatedFte: number;                // derived from assigned class count
  fteMatch: 'match' | 'over' | 'under' | 'discuss' | 'unknown';

  // Day preference
  dayOffPreference: string;            // label for display
  allocatedDays: string[];             // days the teacher is scheduled to teach (derived from line days)
  dayConflictCount: number;            // how many allocated lines run on a day they wanted off
  dayMatch: 'match' | 'conflict' | 'discuss' | 'unknown';

  // Top curriculum area
  topAreaRank1: string | null;
  topAreaMet: 'met' | 'not-met' | 'no-preference';

  // Subject preferences
  topSubjectRank1: string | null;
  topSubjectMet: 'met' | 'not-met' | 'no-preference';

  // RUAH satisfaction
  ruahPreferenceCount: number;
  ruahAllocatedCount: number;
  ruahSatisfaction: 'full' | 'partial' | 'none' | 'no-preference';

  // Overall allocated subject count (for drill-down)
  allocatedSubjectNames: string[];

  // Timing
  isLate: boolean;
  hrDiscussFlag: boolean;
}

export interface PreferenceAnalyticsSummary {
  totalTeachersWithIntentions: number;
  totalTeachersAllocated: number;

  fteMatchRate: number;       // 0..1, share of allocated teachers whose FTE is within tolerance
  fteOverAllocated: number;
  fteUnderAllocated: number;

  dayMatchRate: number;       // share whose allocated lines don't conflict with day-off preference
  dayConflictCount: number;

  topAreaMatchRate: number;   // share of teachers whose #1 curriculum area was honoured
  topSubjectMatchRate: number;
  ruahSatisfactionRate: number; // share of teachers with a RUAH preference who got at least one

  lateSubmissionCount: number;
  discussFlagCount: number;
}

export interface PreferenceAnalyticsResult {
  summary: PreferenceAnalyticsSummary;
  rows: TeacherMatchRow[];
}

// Tolerance for FTE match — within this the teacher is considered a match
const FTE_TOLERANCE = 0.05;

// Hardcoded minutes-per-line (matches the app's DEFAULT_MINUTES_PER_LINE = 220)
// Used when deriving allocated FTE from class count.
const DEFAULT_MINUTES_PER_LINE = 220;
const DEFAULT_BASE_MINUTES = 2220;

function daysOffFromPreference(pref?: DayOffPreference): string[] | null {
  if (!pref) return null;
  switch (pref) {
    case 'wed-thu':  return ['Wednesday', 'Thursday'];
    case 'mon-fri':  return ['Monday', 'Friday'];
    case 'wed-only': return ['Wednesday'];
    case 'fri-only': return ['Friday'];
    case 'none':     return [];
    case 'discuss':  return null;
    default:         return null;
  }
}

function fteLabel(pref: string | undefined, numeric: number | null): string {
  if (pref === 'discuss') return 'discuss';
  if (pref) return pref;
  if (numeric !== null) return numeric.toFixed(2);
  return '—';
}

function classifyFte(preferred: number | null, allocated: number, isDiscuss: boolean): TeacherMatchRow['fteMatch'] {
  if (isDiscuss) return 'discuss';
  if (preferred === null) return 'unknown';
  if (Math.abs(preferred - allocated) <= FTE_TOLERANCE) return 'match';
  return allocated > preferred ? 'over' : 'under';
}

function extractAllocatedSubjectsAndDays(
  grid: StaffAllocationGrid | null,
  teacherCode: string
): { subjects: string[]; days: string[]; lineCount: number } {
  if (!grid) return { subjects: [], days: [], lineCount: 0 };

  const subjects = new Set<string>();
  const days = new Set<string>();
  let lineCount = 0;

  for (const cls of grid.classes || []) {
    const assignments = cls.lineAssignments || {};
    for (const [lineId, assigned] of Object.entries(assignments)) {
      if (!assigned) continue;
      // assigned can be a plain teacher code string OR a JSON array for multi-teacher
      let codes: string[] = [];
      if (typeof assigned === 'string') {
        if (assigned.trim().startsWith('[') || assigned.trim().startsWith('{')) {
          try {
            const parsed = JSON.parse(assigned);
            if (Array.isArray(parsed)) codes = parsed.map((a: any) => a?.teacherCode || a).filter(Boolean);
            else if (parsed?.teacherCode) codes = [parsed.teacherCode];
          } catch {
            codes = [assigned];
          }
        } else {
          codes = [assigned];
        }
      }

      if (codes.includes(teacherCode)) {
        lineCount++;
        const subj = (cls as any).className || (cls as any).subjectCode || 'Unknown';
        subjects.add(subj);
        const line = (grid.lines || []).find(l => l.id === lineId);
        for (const d of (line?.days || [])) days.add(d);
      }
    }
  }

  return {
    subjects: [...subjects].sort(),
    days: [...days].sort(),
    lineCount
  };
}

function subjectMatchesPreference(allocatedSubjects: string[], preference: string): boolean {
  const prefLower = preference.toLowerCase();
  return allocatedSubjects.some(s => {
    const sLower = s.toLowerCase();
    // substring in either direction handles "English" vs "Year 10 English"
    return sLower.includes(prefLower) || prefLower.includes(sLower);
  });
}

export function computePreferenceAnalytics(
  intentions: TeachingIntention[],
  grid: StaffAllocationGrid | null
): PreferenceAnalyticsResult {
  const rows: TeacherMatchRow[] = [];

  for (const intention of intentions) {
    const staffCode = intention.staffId;
    const { subjects: allocatedSubjects, days: allocatedDays, lineCount } =
      extractAllocatedSubjectsAndDays(grid, staffCode);

    // FTE
    const ftePref = (intention.availability as any)?.ftePreference as string | undefined;
    const preferredFte = (intention.availability as any)?.fte ?? null;
    const isDiscussFte = ftePref === 'discuss';
    const allocatedFte = (lineCount * DEFAULT_MINUTES_PER_LINE) / DEFAULT_BASE_MINUTES;
    const fteMatch = classifyFte(preferredFte, allocatedFte, isDiscussFte);

    // Day preference
    const dayOffPref = (intention.availability as any)?.dayOffPreference as DayOffPreference | undefined;
    const daysOff = daysOffFromPreference(dayOffPref);
    const isDiscussDay = dayOffPref === 'discuss';
    const dayConflictCount = daysOff ? allocatedDays.filter(d => daysOff.includes(d)).length : 0;
    const dayMatch: TeacherMatchRow['dayMatch'] =
      isDiscussDay ? 'discuss'
      : daysOff === null ? 'unknown'
      : dayConflictCount > 0 ? 'conflict'
      : 'match';

    // Curriculum areas / subjects
    const curriculumAreas = intention.preferences?.curriculumAreas || [];
    const topArea = curriculumAreas.find(a => a.rank === 1) || curriculumAreas[0];
    const topAreaMet: TeacherMatchRow['topAreaMet'] = !topArea
      ? 'no-preference'
      : subjectMatchesPreference(allocatedSubjects, topArea.areaName) ? 'met' : 'not-met';

    const subjects = intention.preferences?.subjects || [];
    const topSubject = subjects.find(s => s.rank === 1) || subjects[0];
    const topSubjectMet: TeacherMatchRow['topSubjectMet'] = !topSubject
      ? 'no-preference'
      : subjectMatchesPreference(allocatedSubjects, topSubject.subjectName) ? 'met' : 'not-met';

    // RUAH
    const ruah = (intention.preferences as any)?.ruahSubjects || [];
    const ruahAllocated = ruah.filter((r: any) => subjectMatchesPreference(allocatedSubjects, r.subjectName));
    const ruahSatisfaction: TeacherMatchRow['ruahSatisfaction'] =
      ruah.length === 0
        ? 'no-preference'
        : ruahAllocated.length === ruah.length ? 'full'
        : ruahAllocated.length > 0 ? 'partial'
        : 'none';

    rows.push({
      staffId: staffCode,
      staffName: intention.staffName,
      department: intention.department,
      preferredFte,
      preferredFteLabel: fteLabel(ftePref, preferredFte),
      allocatedFte,
      fteMatch,
      dayOffPreference: dayOffPref || '—',
      allocatedDays,
      dayConflictCount,
      dayMatch,
      topAreaRank1: topArea?.areaName || null,
      topAreaMet,
      topSubjectRank1: topSubject?.subjectName || null,
      topSubjectMet,
      ruahPreferenceCount: ruah.length,
      ruahAllocatedCount: ruahAllocated.length,
      ruahSatisfaction,
      allocatedSubjectNames: allocatedSubjects,
      isLate: intention.isLate === true,
      hrDiscussFlag: intention.hrDiscussFlag === true
    });
  }

  // Summary aggregates
  const allocatedRows = rows.filter(r => r.allocatedSubjectNames.length > 0);
  const allocated = allocatedRows.length;
  const safeRate = (numerator: number, denominator: number) =>
    denominator === 0 ? 0 : numerator / denominator;

  const fteMatches = allocatedRows.filter(r => r.fteMatch === 'match').length;
  const fteOver = allocatedRows.filter(r => r.fteMatch === 'over').length;
  const fteUnder = allocatedRows.filter(r => r.fteMatch === 'under').length;

  const dayMatches = allocatedRows.filter(r => r.dayMatch === 'match').length;
  const dayConflicts = allocatedRows.filter(r => r.dayMatch === 'conflict').length;

  const topAreaCandidates = allocatedRows.filter(r => r.topAreaMet !== 'no-preference');
  const topAreaMet = topAreaCandidates.filter(r => r.topAreaMet === 'met').length;
  const topSubjectCandidates = allocatedRows.filter(r => r.topSubjectMet !== 'no-preference');
  const topSubjectMet = topSubjectCandidates.filter(r => r.topSubjectMet === 'met').length;

  const ruahCandidates = rows.filter(r => r.ruahPreferenceCount > 0);
  const ruahSatisfied = ruahCandidates.filter(r => r.ruahSatisfaction === 'full' || r.ruahSatisfaction === 'partial').length;

  const summary: PreferenceAnalyticsSummary = {
    totalTeachersWithIntentions: rows.length,
    totalTeachersAllocated: allocated,
    fteMatchRate: safeRate(fteMatches, allocated),
    fteOverAllocated: fteOver,
    fteUnderAllocated: fteUnder,
    dayMatchRate: safeRate(dayMatches, allocated),
    dayConflictCount: dayConflicts,
    topAreaMatchRate: safeRate(topAreaMet, topAreaCandidates.length),
    topSubjectMatchRate: safeRate(topSubjectMet, topSubjectCandidates.length),
    ruahSatisfactionRate: safeRate(ruahSatisfied, ruahCandidates.length),
    lateSubmissionCount: rows.filter(r => r.isLate).length,
    discussFlagCount: rows.filter(r => r.hrDiscussFlag).length
  };

  return { summary, rows };
}

/**
 * Convert analytics rows into a CSV string for export / download.
 */
export function analyticsRowsToCsv(rows: TeacherMatchRow[]): string {
  const headers = [
    'Staff ID', 'Staff Name', 'Department',
    'Preferred FTE', 'Allocated FTE', 'FTE Match',
    'Day-off Preference', 'Allocated Days', 'Day Conflicts', 'Day Match',
    'Top Area (#1)', 'Top Area Met',
    'Top Subject (#1)', 'Top Subject Met',
    'RUAH Prefs (count)', 'RUAH Allocated (count)', 'RUAH Satisfaction',
    'Allocated Subjects',
    'Is Late', 'HR Discuss Flag'
  ];

  const escape = (v: unknown) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const lines = [
    headers.join(','),
    ...rows.map(r =>
      [
        r.staffId, r.staffName, r.department,
        r.preferredFteLabel, r.allocatedFte.toFixed(2), r.fteMatch,
        r.dayOffPreference, r.allocatedDays.join(';'), r.dayConflictCount, r.dayMatch,
        r.topAreaRank1 || '', r.topAreaMet,
        r.topSubjectRank1 || '', r.topSubjectMet,
        r.ruahPreferenceCount, r.ruahAllocatedCount, r.ruahSatisfaction,
        r.allocatedSubjectNames.join(';'),
        r.isLate, r.hrDiscussFlag
      ].map(escape).join(',')
    )
  ];
  return lines.join('\n');
}
